import * as ethAddressConverter from '@evmos/address-converter';
import { Wallet } from '@ethersproject/wallet';
import { AccountData, coins, DirectSecp256k1HdWallet, Registry } from '@cosmjs/proto-signing';
import {ethermint as ethermintTypes} from '@tharsis/proto/dist/proto/ethermint/types/v1/account';
import { stringToPath } from '@cosmjs/crypto';
import {
    isDeliverTxSuccess,
    SigningStargateClient,
    QueryClient,
    defaultRegistryTypes,
    GasPrice,
    calculateFee,
} from '@cosmjs/stargate';
import { PrivKeySecp256k1 } from '@keplr-wallet/crypto';
import { Tendermint34Client } from '@cosmjs/tendermint-rpc';
import {createTransaction} from '@evmos/proto';
import { arrayify } from '@ethersproject/bytes';
import { signTransaction as signTransactionEvm } from '@hanchon/evmos-ts-wallet';
import {toBase64, fromBase64} from '@cosmjs/encoding';
import {getQueryRequestInstance, getQueryResponseInstance} from './cosmos_queries';
import allTxs, {getTxInstance, getTxNameFromTypeUrl} from './cosmos_tx';
import {getMsgInstance} from './cosmos_msg';
import {randomElement} from '../../../utils/blockchain';
import * as web3signer from '../../wallet/accounts';
import { ethers } from 'ethers';
import {Any} from 'cosmjs-types/google/protobuf/any';
import {RequestQuery} from 'cosmjs-types/tendermint/abci/types';
import {
  CosmWasmClient,
  SigningCosmWasmClient,
  Contract as CosmWasmContract,
} from "@cosmjs/cosmwasm-stargate";
import {isEthermintMessage, encodeMsgEthereumTxAny, EthCallEncode} from './evmos';
import * as CosmosTypes from './cosmos_types';

const providerMap: any = {};

function createDefaultRegistry(): Registry {
  const evmosTypes = [
    ['/ethermint.types.v1.EthAccount', ethermintTypes.types.v1.EthAccount],
  ];
  // @ts-ignore
  const alltypes = defaultRegistryTypes.concat(evmosTypes);
  return new Registry(alltypes);
}

export async function buildProvider(chainId: string, rpc: string, wallet: any, gasPrice: string, force = false): Promise<SigningStargateClient> {
  if (!force && providerMap[chainId]) {return providerMap[chainId];}
  console.log('--buildProvider', rpc);
  const client = await SigningStargateClient.connectWithSigner(
    rpc,
    wallet,
    {
      registry: createDefaultRegistry(),
      gasPrice: GasPrice.fromString(gasPrice),
    },
  );
  return client;
}

export async function getProvider(chain: any, wallet: any, gasPrice: string, force = false): Promise<SigningStargateClient> {
    if (!force && providerMap[chain.chainId]) {return providerMap[chain.chainId];}
    const rpc = randomElement(chain.apis.rpc)[0];
    return buildProvider(chain.chainId, rpc, wallet, gasPrice, force);
}

function parseFee(fee: any) {
  return fee = (fee !== 'auto' && typeof fee === 'string') ? parseFloat(fee) : fee;
}

export async function sendTokens(client: SigningStargateClient, firstAccount: any, recipient: string, amount: any, fee: any, memo: string) {
  try {
    const result = await client.sendTokens(
        firstAccount.address,
        recipient,
        amount,
        parseFee(fee),
        memo,
    );
    const success = isDeliverTxSuccess(result);
    return [success, result];
  } catch(e: any) {
    return {error: e.message};
  }
}

export async function getAccounts({selectedChain}: {selectedChain: CosmosTypes.CosmosChainInfo}) {
  const wallet = await getCosmosWallet(selectedChain.slip44, selectedChain.bech32_prefix, false);
  if (!wallet) {return {error: 'no profile selected'}};

  if (selectedChain.slip44 == 60) {
    const hdpathString = "m/44'/" + selectedChain.slip44.toString() + "'/0'/0/0";
    const _wallet = Wallet.fromMnemonic(wallet.mnemonic, hdpathString);
    const privKey = new PrivKeySecp256k1(arrayify(_wallet.privateKey));
    const pubKey = toBase64(privKey.getPubKey().toBytes());
    return {
      address: _wallet.address,
      algo: 'ethsecp256k1',
      pubkey: pubKey,
    }
  }

  const [firstAccount] = await wallet.getAccounts();
  return [firstAccount];
}

export async function rpcRequestSend (client: SigningStargateClient, sender: string, msgAny: CosmosTypes.CosmosAnyMsg, fee: any, memo: string) {
  console.log('---rpcRequestSend-', sender, [msgAny], fee, memo);
  try {
    const result = await client.signAndBroadcast(sender, [msgAny], parseFee(fee), memo);
    console.log('--result', result);
    const success = isDeliverTxSuccess(result);
    return [success, result];
  } catch (e: any) {
    console.error(e);
    return [false, {error: e.message}];
  }
}

async function sendCosmosQuery(request: CosmosTypes.CosmosRequest) {
  let response: any;
  const {typeUrl: queryPath, selectedChain: chain, value} = request;
  const rpc = randomElement(chain.apis.rpc)[0];
  console.log('---sendCosmosQuery--', queryPath, value, rpc.address);

  const tmClient = await Tendermint34Client.connect(rpc.address);
  const client = QueryClient.withExtensions(tmClient);
  const instance = getQueryRequestInstance(queryPath);

  if (!instance) {return 'not implemented';}

  const encodedRequest = Uint8Array.from(
    instance.encode(
      instance.fromJSON(value),
    ).finish(),
  );

  try {
    const encodedResult = await client.queryUnverified(queryPath, encodedRequest);
    const resultInstance = getQueryResponseInstance(queryPath);
    if (!resultInstance) {return encodedResult;}
    response = resultInstance.decode(encodedResult);
  } catch (e: any) {
    console.error(e);
    return {error: e.message};
  }
  console.log('-----response--', response);
  return response;
}

async function sendEthermintQuery(request: CosmosTypes.CosmosRequest) {
  let response: any;
  const {typeUrl: queryPath, selectedChain: chain, value} = request;
  const rpc = randomElement(chain.apis.rpc)[0];
  console.log('---sendEthermintQuery--', queryPath, value, rpc.address);

  const tmClient = await Tendermint34Client.connect(rpc.address);
  const client = QueryClient.withExtensions(tmClient);

  const encodedRequest = EthCallEncode(value);
  console.log('---sendEthermintQuery', queryPath, encodedRequest)

  try {
    const encodedResult = await client.queryUnverified(queryPath, encodedRequest);
    // const resultInstance = getQueryResponseInstance(queryPath);
    // if (!resultInstance) {return encodedResult;}
    // response = resultInstance.decode(encodedResult);
    response = {result: encodedResult};
  } catch (e: any) {
    console.error(e);
    return {error: e.message};
  }
  console.log('-----response--', response);
  return response;
}

async function getSender(restApi: string, bech32Address: string, privateKey?: string) {
  const response = await fetch(`${restApi}/cosmos/auth/v1beta1/accounts/` + bech32Address);
  let data;
  try {
    data = await response.json();
  } catch (e) {
    return {error: 'Rest API does not support account retrieval'};
  }
  console.log('--data', data);
  // no code or code == 0 is ok
  if (data.code) {
    return {error: data.message + ' or REST API does not support account retrieval'};
  }
  if (!data.account.base_account.pub_key && privateKey) {
    const privKey = new PrivKeySecp256k1(arrayify(privateKey));
    const pubKey = toBase64(privKey.getPubKey().toBytes());
    data.account.base_account.pub_key = {
      key: pubKey,
    };
  }
  return data;
}

async function broadcastEvm(restApi: string, resKeplr: string) {
  let broadcastRes;
  try {
    const post = await fetch(`${restApi}/cosmos/tx/v1beta1/txs`, {
      method: 'post',
      body: resKeplr,
      headers: { 'Content-Type': 'application/json' },
    });
    broadcastRes = await post.json();
  } catch (e: any) {
    console.error('broadcast error:', e);
    broadcastRes = {};
  }
  if (broadcastRes.tx_response.code === 0) {
      console.log('Success', broadcastRes);
      return [true, broadcastRes.tx_response];
  } else {
      console.error('Error', broadcastRes);
      return [false, {error: broadcastRes.tx_response?.raw_log || 'broadcast error', broadcastRes}];
  }
}

async function estimateEvm(restApi: string, resKeplr: string) {
  let broadcastRes;
  try {
    const post = await fetch(`${restApi}/cosmos/tx/v1beta1/simulate`, {
      method: 'post',
      body: resKeplr,
      headers: { 'Content-Type': 'application/json' },
    });
    broadcastRes = await post.json();
    console.log('--estimateEvm', broadcastRes);
    return broadcastRes.gas_info.gas_used;
  } catch (e: any) {
    console.error('broadcast error:', e);
    return 0;
  }
}

async function signEvm(wallet: any, msgAny: any, memo: any, fees: string, denom: string, gasLimit: number, pubKey: string, sequence: number, accountNumber: number, chainId: string): Promise<string> {
  console.log('---------****signEvm');
  const algo = 'ethsecp256k1';
  const tx = createTransaction(msgAny, memo, fees, denom, gasLimit, algo, pubKey, sequence, accountNumber, chainId);
  console.log('----createTransaction', tx);
  // @ts-ignore
  return signTransactionEvm(wallet, tx);
}

// gasPrice - only value, no denom
async function signAndEstimateEvm(
  restApi: string, wallet: any,
  msgAny: any, memo: any,
  feesMultiplier: number,
  fees: string | undefined,
  gasLimit: number | undefined,
  denom: string,
  gasPrice: string,
  pubKey: string, sequence: number, accountNumber: number,
  chainId: string,
): Promise<string> {
  if (!gasLimit) {
    gasLimit = 1;
    let simFees = fees;
    if (!simFees) {
      // TODO - this fails if fees are < than needed
      // and fails if fees are > than balance ...
      // find another API
      simFees = '10000000000000000';
    }

    console.log('---signAndEstimateEvm', msgAny, memo, simFees, denom, gasLimit, pubKey, sequence, accountNumber, chainId);

    const txString = await signEvm(wallet, msgAny, memo, simFees, denom, gasLimit, pubKey, sequence, accountNumber, chainId);
    console.log('----txString', txString);
    const gasUsed = await estimateEvm(restApi, txString);

    gasLimit = Math.ceil(gasUsed * feesMultiplier);
  }
  if (!fees) {
    const stdFees = calculateFee(gasLimit, gasPrice);
    fees = stdFees.amount[0].amount;
    console.log('---fees', fees);
  }

  return signEvm(wallet, msgAny, memo, fees, denom, gasLimit, pubKey, sequence, accountNumber, chainId);
}

function msgAnyFromTypeUrl(request: CosmosTypes.CosmosRequest) {
  // set by encodeMsgEthereumTxAny
  if (request.typeUrl.includes('MsgEthereumTx')) return request.value;

  const queryName = getTxNameFromTypeUrl(request.typeUrl);
  if (!allTxs[queryName]) {return null;}

  if (queryName === 'MsgSubmitProposal') {
    if (request.value.content && request.value.content.typeUrl) {
      const {typeUrl, value} = request.value.content;
      const instance = getMsgInstance(typeUrl);
      const msg = instance.fromJSON(value);
      console.log('---msg--', msg);

      request.value.content.value = Uint8Array.from(
        instance.encode(
          instance.fromJSON(value),
        ).finish(),
      );
    }
  }

  const msg: any = {};
  msg.serializeBinary = () => {
    console.log('--serialize', queryName, request.value);
    console.log('---serialize fromjson', allTxs[queryName].fromJSON(request.value));
    return Uint8Array.from(
      allTxs[queryName].encode(
        allTxs[queryName].fromJSON(request.value),
      ).finish(),
    );
  };

  const msgAny: any = {
    path: request.typeUrl.slice(1),
    message: msg,
  };
  return msgAny;
}

// TODO sign with something else than first wallet address
async function evmTransact(mnemonic: string, hdpathString: string, request: CosmosTypes.CosmosRequest) {
  const chain = request.selectedChain;
  const wallet = Wallet.fromMnemonic(mnemonic, hdpathString);
  // const evmosAddress = ethToEvmos(wallet.address);

  const _data = ethAddressConverter.ETH.decoder(wallet.address);
  // @ts-ignore
  const CHAIN = ethAddressConverter.bech32Chain(chain.name, chain.bech32_prefix);
  const evmosAddress = CHAIN.encoder(_data);

  const restApi = randomElement(chain.apis.rest)[0].address;
  const denom = chain.nativeCurrency.name;
  const feesMultiplier = typeof request.fee === 'string' ? parseFloat(request.fee) : 1.3;
  let fees, gasLimit;
  if (typeof request.fee !== 'string') {
    gasLimit = parseInt(request.fee.gas);
    fees = request.fee.amount[0].amount;
  }
  // fee:  "1.2"
  // gasPrice: "1000agoric"

  console.log('address', wallet.address, evmosAddress);
  console.log('----restApi', restApi, chain.apis.rest);

  const senderAccount = await getSender(restApi, evmosAddress, wallet.privateKey);
  console.log('account', senderAccount);
  if (!senderAccount) {
    return [false, {error: 'Account does not exist: ' + evmosAddress}];
  }
  if (senderAccount.error) {return [false, senderAccount];}
  const baseAccount = senderAccount.account.base_account;

  const msgAny = msgAnyFromTypeUrl(request);
  if (!msgAny) {return [false, {error: 'TypeUrl not implemented: ' + request.typeUrl}];}

  const resKeplr = await signAndEstimateEvm(restApi, wallet, msgAny, request.memo, feesMultiplier, fees, gasLimit, denom, request.gasPrice, baseAccount.pub_key.key, baseAccount.sequence, baseAccount.account_number, chain.chainId);
  console.log('----resKeplr', resKeplr);
  return broadcastEvm(restApi, resKeplr);
}

export async function encodeCosmosTx (request: CosmosTypes.CosmosAnyMsg) {
  const {typeUrl, value} = request;
  const txName = getTxNameFromTypeUrl(typeUrl);
  const instance = getTxInstance(typeUrl);
  if (!instance) {return 'not implemented';}

  if (txName === 'MsgSubmitProposal') {
    if (value.content && ethers.utils.isHexString(value.content)) {
      const content = Any.decode(ethers.utils.arrayify(value.content));
      value.content = content;
    }
    else if (value.content && value.content.typeUrl) {
      value.content.value = _encodeCosmosMsg(value.content);
    }
  }
  console.log('--encodeCosmosTx-value-', value);

  const bzval = Uint8Array.from(
    instance.encode(
      instance.fromJSON(value),
    ).finish(),
  );
  const msg = {typeUrl, value: bzval};
  const bz =  Uint8Array.from(Any.encode(msg).finish());
  return ethers.utils.hexlify(bz);
}

// e.g. /cosmos.bank.v1beta1.Query/Balance
export async function encodeCosmosQuery (request: CosmosTypes.CosmosAnyMsg) {
  const {typeUrl: queryPath, value} = request;
  console.log('encodeCosmosQuery', queryPath);

  const instance = getQueryRequestInstance(queryPath)
  if (!instance) {return 'not implemented';}

  const bzval = Uint8Array.from(
    instance.encode(
      instance.fromJSON(value),
    ).finish(),
  );

  // const msg = {typeUrl, value: bzval};
  // console.log('encodeCosmosQuery msg', msg);
  // const bz =  Uint8Array.from(Any.encode(msg).finish());

  // const decoded = Any.decode(bz);
  // console.log('encodeCosmosQuery decoded', decoded);

  const bz = Uint8Array.from(
    RequestQuery.encode(
      RequestQuery.fromJSON({
        data: bzval,
        path: queryPath,
      }),
    ).finish(),
  );

  return ethers.utils.hexlify(bz);
}

function _encodeCosmosMsg(request: CosmosTypes.CosmosAnyMsg) {
  const instance = getMsgInstance(request.typeUrl);
  const msg = instance.fromJSON(request.value);
  console.log('---msg--', msg);

  return Uint8Array.from(
    instance.encode(
      instance.fromJSON(request.value),
    ).finish(),
  );
}

function encodeCosmosAnyMsg(request: CosmosTypes.CosmosAnyMsg) {
  const bzval = _encodeCosmosMsg(request);
  const msg = {typeUrl: request.typeUrl, value: bzval};
  const bz =  Uint8Array.from(Any.encode(msg).finish());
  return ethers.utils.hexlify(bz);
}

export async function encodeCosmosRequest (request: CosmosTypes.CosmosAnyMsg) {
  if (request.typeUrl.includes('Query')) {
    return encodeCosmosQuery(request);
  }
  if (getMsgInstance(request.typeUrl)) {
    return encodeCosmosAnyMsg(request);
  }
  return encodeCosmosTx(request);
}

function decodeCosmosQuery(msg: CosmosTypes.CosmosDecodeMsg) {
  console.log('decodeCosmosQuery---msg--', msg);
  if (msg.type === 'request') return 'Not implemented';
  const encodedResult = ethers.utils.arrayify(msg.value);
  const queryPath = msg.typeUrl;
  const resultInstance = getQueryResponseInstance(queryPath);
  if (!resultInstance) {return msg.value;}

  let response;
  try {
    response = resultInstance.decode(encodedResult);
  } catch (e: any) {
    console.error(e);
    return {error: e.message};
  }
  console.log('-----response--', response);
  return response;
}

function decodeCosmosAnyMsg(msg: CosmosTypes.CosmosDecodeMsg) {
  return 'Not implemented';
}

function decodeCosmosTx(msg: CosmosTypes.CosmosDecodeMsg) {
  return 'Not implemented';
}

export function decodeCosmosData (msg: CosmosTypes.CosmosDecodeMsg) {
  if (!msg) return;
  if (msg.typeUrl.includes('Query')) {
    return decodeCosmosQuery(msg);
  }
  if (getMsgInstance(msg.typeUrl)) {
    return decodeCosmosAnyMsg(msg);
  }
  return decodeCosmosTx(msg);
}

async function getCosmosWallet(slip44: number, bech32Prefix: string, forceAsk = true): Promise<DirectSecp256k1HdWallet | undefined> {
  const profileIndex = await web3signer.getCurrentProfileIndex();
  if (profileIndex === null) return;
  const _wallet = await web3signer.getProfileWallet(profileIndex, 'cosmos request', forceAsk);
  const {mnemonic} = _wallet;
  const hdpathString = "m/44'/" + slip44.toString() + "'/0'/0/0";
  const walletOptions = {
    bip39Password: '',
    hdPaths: [
        stringToPath(hdpathString),
    ],
    prefix: bech32Prefix,
  };
  const wallet = await DirectSecp256k1HdWallet.fromMnemonic(
      mnemonic,
      walletOptions,
  );
  return wallet;
}

export async function sendCosmosRequest (request: CosmosTypes.CosmosRequest) {
  console.log('---sendCosmosRequest--', request);
  if (!request.typeUrl || !request.value || !request.selectedChain) {return;}

  // TODO fixme
  if (request.typeUrl.includes('Query')) {
    if (request.typeUrl.includes('ethermint')) return sendEthermintQuery(request);
    return sendCosmosQuery(request);
  }

  if (isEthermintMessage(request.typeUrl)) {
    request.value = encodeMsgEthereumTxAny(request.typeUrl, request.value);
  }
  console.log('---sendCosmosRequest2--', request);

  const chain = request.selectedChain;
  const wallet = await getCosmosWallet(chain.slip44, chain.bech32_prefix);
  if (!wallet) {return 'no profile selected';}

  const [firstAccount] = await wallet.getAccounts();
  console.log('firstAccount', firstAccount, chain.slip44);

  if (chain.slip44 == 60) {
    const hdpathString = "m/44'/" + chain.slip44.toString() + "'/0'/0/0";
    return evmTransact(wallet.mnemonic, hdpathString, request);
  }

  const client = await getProvider(chain, wallet, request.gasPrice);

  const msgAny: CosmosTypes.CosmosAnyMsg = {
    typeUrl: request.typeUrl,
    value: request.value,
  };
  return rpcRequestSend(client, firstAccount.address, msgAny, request.fee, request.memo);
}

// "/ethermint.crypto.v1.ethsecp256k1.PubKey"
// "/cosmos.crypto.secp256k1.PubKey",

///////*********CosmWasm */

interface ClientConnection {
    client: CosmWasmClient;
    rpcEndpoint: string;
}

interface SigningClientConnection extends ClientConnection {
    client: SigningCosmWasmClient;
    address: string;
}

class ConnectionManager {
    private queryingClientConnection: ClientConnection | undefined;
    private signingClientConnections: { [key: string]: SigningClientConnection } =
        {};

    getQueryClient = async (
        chain: CosmosTypes.CosmosChainInfo,
        forceRefresh = false
    ): Promise<CosmWasmClient> => {
      const rpcEndpoint = randomElement(chain.apis.rpc)[0].address;
        if (
            this.queryingClientConnection === undefined ||
            this.queryingClientConnection.rpcEndpoint !== rpcEndpoint ||
            forceRefresh
        ) {
            this.queryingClientConnection = {
                client: await CosmWasmClient.connect(rpcEndpoint),
                rpcEndpoint,
            };
        }

        return this.queryingClientConnection.client;
    };

    getSigningClient = async (
        wallet: DirectSecp256k1HdWallet,
        chain: CosmosTypes.CosmosChainInfo,
        gasPrice: string,
    ): Promise<SigningCosmWasmClient> => {
        const rpcEndpoint = randomElement(chain.apis.rpc)[0].address;
        const [account] = await wallet.getAccounts();
        const address = account.address;
        if (
            this.signingClientConnections[address] === undefined ||
            this.signingClientConnections[address].rpcEndpoint !== rpcEndpoint
        ) {
            this.signingClientConnections[address] = {
                client: await SigningCosmWasmClient.connectWithSigner(
                    rpcEndpoint,
                    wallet,
                    {
                        // @ts-ignore
                        gasPrice: GasPrice.fromString(gasPrice),
                    }
                ),
                address,
                rpcEndpoint,
            };
        }
        return this.signingClientConnections[address].client;
    };
}

const CWConnectionManager = new ConnectionManager();

async function cwUpload (request: CosmosTypes.CosmWasmRequestUpload) {
  const {selectedChain: chain} = request;

  const wallet = await getCosmosWallet(chain.slip44, chain.bech32_prefix);
  if (!wallet) {return {error: 'no profile selected'}};

  const [firstAccount] = await wallet.getAccounts();
  const client = await CWConnectionManager.getSigningClient(wallet, chain, request.gasPrice);

  let wasmbin = request.value
  if (request.value instanceof Uint8Array) wasmbin = request.value;
  if (typeof request.value === 'string') {
    const prefix = 'data:application/wasm;base64,';
    let _value: string = request.value;
    if (_value.includes(prefix)) {
      _value = _value.slice(29);
    }
    try {
      wasmbin = fromBase64(_value);
    } catch (e: any) {
      console.log(e);
      return {error: e.message};
    }
  }

  try {
    const result = await client.upload(
      firstAccount.address,
      wasmbin,
      parseFee(request.fee),
      request.memo,
    );
    console.log('===wasm upload===', result);
    return result;
  } catch (e: any) {
    console.log(e);
    return {error: e.message};
  }
}

async function cwInstantiate (request: CosmosTypes.CosmWasmRequestInstantiate) {
    const {selectedChain: chain, codeId, value: instantiateMsg, label} = request;
    if (!codeId) return {error: "Code id missing"};
    if (!label) return {error: "Label missing"};

    const wallet = await getCosmosWallet(chain.slip44, chain.bech32_prefix);
    if (!wallet) {return {error: 'no profile selected'}};

    const [firstAccount] = await wallet.getAccounts();
    const client = await CWConnectionManager.getSigningClient(wallet, chain, request.gasPrice);

    try {
      const result = await client.instantiate(
        firstAccount.address,
        codeId,
        instantiateMsg,
        label,
        parseFee(request.fee),
      );
      return result;
    } catch (e: any) {
      console.log(e)
      return {error: e.message};
    }
}

async function cwDeploy (request: CosmosTypes.CosmWasmRequestDeploy) {
  let uploadResult;
  let codeId;
  if (request.value) {
    uploadResult = await cwUpload({
      value: request.value,
      fee: request.fee,
      memo: request.memo,
      selectedChain: request.selectedChain,
      gasPrice: request.gasPrice,
    });

    // @ts-ignore
    if (uploadResult.error) {
      return uploadResult;
    }
    // @ts-ignore
    codeId = uploadResult.codeId;
  }
  if (request.codeId) codeId = request.codeId;

  const instantiateResult = await cwInstantiate({
    value: request.instantiateMsg,
    codeId,
    label: request.label,
    fee: request.fee,
    memo: request.memo,
    selectedChain: request.selectedChain,
    gasPrice: request.gasPrice,
  })

  return {upload: uploadResult, instantiate: instantiateResult};
}

async function getCWContract (client: SigningCosmWasmClient, address: string): Promise<CosmWasmContract | undefined> {
  try {
    const contract = await client.getContract(address);
    return contract;
  } catch (e) {
    return;
  }
}

async function cwExecute(request: CosmosTypes.CosmWasmRequest) {
  const {selectedChain: chain, address} = request;
  const wallet = await getCosmosWallet(chain.slip44, chain.bech32_prefix);
  if (!wallet) {return {error: 'No profile selected'};}

  const client = await CWConnectionManager.getSigningClient(wallet, chain, request.gasPrice);
  const [account] = await wallet.getAccounts();
  try {
    const result = await client.execute(
      account.address,
      address,
      request.value,
      parseFee(request.fee),
      request.memo,
      request.amount,
    );
    return result;
  } catch (e: any) {
    return {error: e.message};
  }
}

async function cwQuery(request: CosmosTypes.CosmWasmRequest) {
  const client = await CWConnectionManager.getQueryClient(
    request.selectedChain,
    true,
  );
  try {
    const result = await client.queryContractSmart(request.address, request.value);
    return result;
  } catch(e: any) {
    return {error: e.message};
  }
}

async function cwGetCodes(request: {selectedChain:CosmosTypes.CosmosChainInfo}) {
  const client = await CWConnectionManager.getQueryClient(
    request.selectedChain,
    true,
  );
  try {
    const result = await client.getCodes();
    return result;
  } catch(e: any) {
    return {error: e.message};
  }
}

async function cwGetCodeDetails(request: {selectedChain:CosmosTypes.CosmosChainInfo, codeId: number}) {
  const client = await CWConnectionManager.getQueryClient(
    request.selectedChain,
    true,
  );
  try {
    const result = await client.getCodeDetails(request.codeId);
    return result;
  } catch(e: any) {
    return {error: e.message};
  }
}

async function cwGetContracts(request: {selectedChain:CosmosTypes.CosmosChainInfo, codeId: number}) {
  const client = await CWConnectionManager.getQueryClient(
    request.selectedChain,
    true,
  );
  try {
    const result = await client.getContracts(request.codeId);
    const contracts = [];
    for (let address of result) {
      const contract = await cwGetContract({selectedChain: request.selectedChain, address});
      // @ts-ignore
      if (contract && !contract.error) {
        contracts.push(contract);
      }
    }
    return contracts;
  } catch(e: any) {
    return {error: e.message};
  }
}

async function cwGetContract(request: {selectedChain:CosmosTypes.CosmosChainInfo, address: string}) {
  const client = await CWConnectionManager.getQueryClient(
    request.selectedChain,
    true,
  );
  try {
    const result = await client.getContract(request.address);
    return result;
  } catch(e: any) {
    return {error: e.message};
  }
}

export async function extension () {
    // await testc();
    // const req: any = {"typeUrl":"/cosmos.gov.v1beta1.MsgSubmitProposal","value":{"content":{"typeUrl":"/cosmos.gov.v1beta1.TextProposal","value":{"title":"titless","description":"descriptionn"}},"proposer":"evmos1na47wxluql4enytzyuut3my69dj5g7c5wwwkme","initialDeposit":null},"fee":"1.3","memo":"","selectedChain":{"name":"Evmos","shortName":"evmos","chain":null,"infoURL":"https://github.com/evmos/evmos","chainId":"evmos_9001-2","networkId":"evmos_9001-2","slip44":60,"fees":{"fee_tokens":[{"denom":"aevmos","low_gas_price":10000000000,"average_gas_price":25000000000,"high_gas_price":40000000000}]},"apis":{"rpc":[{"address":"https://rpc-evmos.ecostake.com","provider":"ecostake"},{"address":"https://rpc-evmos.whispernode.com","provider":" WhisperNode 🤐"},{"address":"https://tendermint.bd.evmos.org:26657","provider":"Blockdaemon"},{"address":"https://rpc.evmos.ezstaking.io","provider":"EZStaking.io"},{"address":"https://rpc-evmos-ia.cosmosia.notional.ventures:443","provider":"Notional"},{"address":"https://rpc.evmos.posthuman.digital","provider":"POSTHUMAN ꝏ DVS"},{"address":"https://rpc.evmos.interbloc.org","provider":"Interbloc"},{"address":"https://rpc.evmos.testnet.run","provider":"TestNetRun"},{"address":"https://rpc.evmos.nodestake.top","provider":"NodeStake"},{"address":"https://rpc.evmos.bh.rocks","provider":"BlockHunters"},{"address":"https://rpc-evmos.goldenratiostaking.net","provider":"Golden Ratio Staking"},{"address":"https://rpc.evmos.chaintools.tech/","provider":"ChainTools"},{"address":"https://evmos-rpc.polkachu.com","provider":"Polkachu"},{"address":"https://rpc.evmos.silknodes.io/","provider":"Silk Nodes"}],"rest":[{"address":"https://rest-evmos.ecostake.com","provider":"ecostake"},{"address":"https://lcd-evmos.whispernode.com","provider":" WhisperNode 🤐"},{"address":"https://rest.bd.evmos.org:1317","provider":"Blockdaemon"},{"address":"https://lcd.evmos.ezstaking.io","provider":"EZStaking.io"},{"address":"https://api-evmos-ia.cosmosia.notional.ventures/","provider":"Notional"},{"address":"https://lcd.evmos.posthuman.digital","provider":"POSTHUMAN ꝏ DVS"},{"address":"https://api.evmos.interbloc.org","provider":"Interbloc"},{"address":"https://api.evmos.testnet.run","provider":"TestNetRun"},{"address":"https://lcd.evmos.bh.rocks","provider":"BlockHunters"},{"address":"https://rpc.evmos.chaintools.tech/","provider":"ChainTools"},{"address":"https://api.evmos.nodestake.top","provider":"NodeStake"},{"address":"https://evmos-api.polkachu.com","provider":"Polkachu"},{"address":"https://api.evmos.silknodes.io/","provider":"Silk Nodes"}],"grpc":[{"address":"https://grpc.bd.evmos.org:9090","provider":"evmos.org"},{"address":"grpc-evmos-ia.cosmosia.notional.ventures:443","provider":"Notional"},{"address":"grpc.evmos.posthuman.digital:443","provider":"POSTHUMAN ꝏ DVS"},{"address":"grpc.evmos.testnet.run:8091","provider":"TestNetRun"},{"address":"https://grpc.evmos.nodestake.top","provider":"NodeStake"},{"address":"https://grpc.evmos.bh.rocks","provider":"BlockHunters"},{"address":"https://evmos.grpcui.chaintools.host/","provider":"ChainTools"},{"address":"evmos-grpc.polkachu.com:13490","provider":"Polkachu"},{"address":"https://grpc.evmos.silknodes.io/","provider":"Silk Nodes"}],"evm-http-jsonrpc":[{"address":"https://eth.bd.evmos.org:8545","provider":"Blockdaemon"},{"address":"https://jsonrpc-evmos-ia.cosmosia.notional.ventures/","provider":"Notional"},{"address":"https://evmos-json-rpc.stakely.io","provider":"Stakely"},{"address":"https://jsonrpc.evmos.nodestake.top","provider":"NodeStake"},{"address":"https://json-rpc.evmos.bh.rocks","provider":"BlockHunters"}]},"explorers":[{"kind":"mintscan","url":"https://www.mintscan.io/evmos","tx_page":"https://www.mintscan.io/evmos/txs/${txHash}"},{"kind":"bigdipper","url":"https://evmos.bigdipper.live","tx_page":"https://evmos.bigdipper.live/transactions/${txHash}"},{"kind":"blockscout","url":"https://evm.evmos.org","tx_page":"https://evm.evmos.org/tx/${txHash}"},{"kind":"ping-pub","url":"https://ping.pub/evmos","tx_page":"https://ping.pub/evmos/tx/${txHash}"},{"kind":"explorers.guru","url":"https://evmos.explorers.guru","tx_page":"https://evmos.explorers.guru/transaction/${txHash}"},{"kind":"atomscan","url":"https://atomscan.com/evmos","tx_page":"https://atomscan.com/evmos/transactions/${txHash}"}],"network_type":"mainnet","status":"live","bech32_prefix":"evmos","nativeCurrency":{"description":"The native EVM, governance and staking token of the Evmos Hub","denom_units":[{"denom":"aevmos","exponent":0},{"denom":"evmos","exponent":18}],"base":"aevmos","name":"aevmos","display":"evmos","symbol":"EVMOS","logo_URIs":{"svg":"https://raw.githubusercontent.com/cosmos/chain-registry/master/evmos/images/evmos.svg","png":"https://raw.githubusercontent.com/cosmos/chain-registry/master/evmos/images/evmos.png"},"coingecko_id":"evmos"},"label":"Evmos"},"gasPrice":"25000000000aevmos"}

    // const req = {"typeUrl":"/cosmos.gov.v1beta1.MsgSubmitProposal","value":{"content":{"typeUrl":"/cosmos.gov.v1beta1.TextProposal","value":{"title":"Proposal Title","description":"## Description\\n\\nSomething here too\\nwith \"quotes\"."}},"proposer":"mythos1na47wxluql4enytzyuut3my69dj5g7c5nqvmup","initialDeposit":[{"amount":0,"denom":"aMYT"}]},"fee":"1.3","memo":"","selectedChain":{"name":"Mythos","shortName":"mythos","chain":null,"infoURL":null,"chainId":"mythos_7001-1","networkId":"mythos_7001-1","slip44":60,"fees":{"fee_tokens":[{"denom":"aMYT"}]},"apis":{"rpc":[{"address":"https://mythos-rpc.provable.dev:443/","provider":"laurel.provable"}],"rest":[{"address":"https://mythos.provable.dev/rest/","provider":"laurel.provable"}],"grpc":[{"address":"https://mythos-grpc.provable.dev:443","provider":"laurel.provable"}],"evm-http-jsonrpc":[{"address":"https://mythos-evm.provable.dev/","provider":"laurel.provable"}]},"explorers":[],"network_type":"mainnet","status":"live","bech32_prefix":"mythos","nativeCurrency":{"description":"The native token of Mythos","denom_units":[{"denom":"aMYT","exponent":0},{"denom":"MYT","exponent":18}],"base":"aMYT","name":"aMYT","display":"MYT","symbol":"MYT","logo_URIs":{"svg":"https://raw.githubusercontent.com/cosmos/chain-registry/master/mythos/images/mythos.svg","png":"https://raw.githubusercontent.com/cosmos/chain-registry/master/mythos/images/mythos.png"}},"label":"Mythos"},"gasPrice":"1000aMYT"}


    // const req = {"typeUrl":"/cosmos.gov.v1beta1.MsgSubmitProposal","value":{"content":{"typeUrl":"/cosmos.gov.v1beta1.TextProposal","value":{"title":"A prop title","description":"A prop description"}},"proposer":"evmos1na47wxluql4enytzyuut3my69dj5g7c5wwwkme","initialDeposit":null},"fee":"1.3","memo":"","selectedChain":{"name":"Evmos Testnet","shortName":"evmostestnet","chain":null,"infoURL":"https://github.com/evmos/evmos","chainId":"evmos_9000-4","networkId":"evmos_9000-4","slip44":60,"fees":{"fee_tokens":[{"denom":"atevmos","low_gas_price":20000000000,"average_gas_price":25000000000,"high_gas_price":40000000000}]},"apis":{"rpc":[{"address":"https://evmos.test.rpc.coldyvalidator.net","provider":"coldy"},{"address":"https://evmos-testnet-rpc.polkachu.com","provider":"Polkachu"}],"rest":[{"address":"https://evmos.test.api.coldyvalidator.net","provider":"coldy"},{"address":"https://evmos-testnet-api.polkachu.com","provider":"Polkachu"}],"grpc":[{"address":"https://evmos.test.grpc.coldyvalidator.net","provider":"coldy"}]},"explorers":[{"kind":"Mintscan","url":"https://testnet.mintscan.io/evmos-testnet","tx_page":"https://testnet.mintscan.io/evmos-testnet/txs/${txHash}"},{"kind":"NodesGuru","url":"https://testnet.evmos.explorers.guru/","tx_page":"https://testnet.evmos.explorers.guru/transaction/${txHash}"}],"network_type":"testnet","status":"live","bech32_prefix":"evmos","nativeCurrency":{"description":"The native EVM, governance and staking token of the Evmos Hub","denom_units":[{"denom":"atevmos","exponent":0},{"denom":"tevmos","exponent":18}],"base":"atevmos","name":"atevmos","display":"tevmos","symbol":"TEVMOS","logo_URIs":{"png":"https://raw.githubusercontent.com/cosmos/chain-registry/master/testnets/evmostestnet/images/evmos.png","svg":"https://raw.githubusercontent.com/cosmos/chain-registry/master/testnets/evmostestnet/images/evmos.svg"}},"label":"Evmos Testnet"},"gasPrice":"25000000000atevmos"};

    // await sendCosmosRequest(req);

    const extensions = {
        label: 'cosmos',
        items: [
          {
            label: 'getAccounts',
            value: getAccounts,
          },
            {
                label: 'request',
                value: sendCosmosRequest,
            },
            {
              label: 'encode',
              value: encodeCosmosRequest,
            },
            {
              label: 'decode',
              value: decodeCosmosData,
            },
            {
                label: 'send',
                value: sendTokens,
            },
            {
              label: 'cosmwasm-upload',
              value: cwUpload,
            },
            {
              label: 'cosmwasm-instantiate',
              value: cwInstantiate,
            },
            {
              label: 'cosmwasm-deploy',
              value: cwDeploy,
            },
            {
              label: 'cosmwasm-execute',
              value: cwExecute,
            },
            {
              label: 'cosmwasm-query',
              value: cwQuery,
            },
            {
              label: 'cosmwasm-query-getCodes',
              value: cwGetCodes,
            },
            {
              label: 'cosmwasm-query-getCodeDetails',
              value: cwGetCodeDetails,
            },
            {
              label: 'cosmwasm-query-getContracts',
              value: cwGetContracts,
            },
            {
              label: 'cosmwasm-query-getContract',
              value: cwGetContract,
            },
        ],
    };

    return {extensions};
}
